import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  clientIntegrationsAPI,
  notificationChannelsAPI,
} from "../../services/api";
import { useCan } from "../../hooks/useCan";
import {
  Link2,
  Unlink,
  Trash2,
  TestTube,
  Loader2,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";

// ── Vendor logos (inline SVGs) ─────────────────────────────────────────────
const VapiLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="#0F172A" />
    <path d="M60 60L100 140L140 60" stroke="#22D3EE" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="100" cy="52" r="8" fill="#22D3EE" />
  </svg>
);

const RetellLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="#4F46E5" />
    <path d="M60 70H120C131 70 140 79 140 90C140 101 131 110 120 110H80V140" stroke="white" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="130" cy="135" r="10" fill="#A5B4FC" />
  </svg>
);

const SlackLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="white" stroke="#E2E8F0" strokeWidth="2" />
    <path d="M72 116C72 108.3 64.3 102 58 102C51.4 102 45 108.3 45 116C45 123.7 51.4 130 58 130H72V116Z" fill="#E01E5A" />
    <path d="M80 116C80 123.7 86.3 130 93 130C99.7 130 106 123.7 106 116V72C106 64.3 99.7 58 93 58C86.3 58 80 64.3 80 72V116Z" fill="#E01E5A" />
    <path d="M93 72C100.7 72 107 64.3 107 58C107 51.3 100.7 45 93 45C85.3 45 79 51.3 79 58V72H93Z" fill="#36C5F0" />
    <path d="M93 80C85.3 80 79 86.3 79 93C79 99.7 85.3 106 93 106H137C144.7 106 151 99.7 151 93C151 86.3 144.7 80 137 80H93Z" fill="#36C5F0" />
    <path d="M137 93C137 85.3 143.7 79 151 79C158.3 79 164 85.3 164 93C164 100.7 158.3 107 151 107H137V93Z" fill="#2EB67D" />
    <path d="M129 93C129 85.3 122.7 79 116 79C109.3 79 103 85.3 103 93V137C103 144.7 109.3 151 116 151C122.7 151 129 144.7 129 137V93Z" fill="#2EB67D" />
    <path d="M116 137C108.3 137 102 143.7 102 151C102 158.3 108.3 164 116 164C123.7 164 130 158.3 130 151V137H116Z" fill="#ECB22E" />
    <path d="M116 129C123.7 129 130 122.7 130 116C130 109.3 123.7 103 116 103H72C64.3 103 58 109.3 58 116C58 122.7 64.3 129 72 129H116Z" fill="#ECB22E" />
  </svg>
);

const EmailLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="2" />
    <rect x="40" y="60" width="120" height="80" rx="10" stroke="#4F46E5" strokeWidth="8" />
    <path d="M40 70L100 110L160 70" stroke="#4F46E5" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WebhookLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="#F0FDF4" stroke="#BBF7D0" strokeWidth="2" />
    <circle cx="100" cy="80" r="20" stroke="#16A34A" strokeWidth="8" />
    <path d="M100 100V150" stroke="#16A34A" strokeWidth="8" strokeLinecap="round" />
    <path d="M70 130L100 150L130 130" stroke="#16A34A" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Provider definitions ───────────────────────────────────────────────────
const LiveKitLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="#0D1117" />
    <path d="M60 60V140" stroke="#FF6B6B" strokeWidth="16" strokeLinecap="round" />
    <path d="M100 80V140" stroke="#FF6B6B" strokeWidth="16" strokeLinecap="round" />
    <path d="M140 100V140" stroke="#FF6B6B" strokeWidth="16" strokeLinecap="round" />
  </svg>
);

const DailyLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="#1A73E8" />
    <circle cx="100" cy="100" r="40" stroke="white" strokeWidth="12" />
    <circle cx="100" cy="100" r="12" fill="white" />
  </svg>
);

const PlivoLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="#12203A" />
    <path d="M60 50V150" stroke="#00C58E" strokeWidth="16" strokeLinecap="round" />
    <path d="M60 50H110C127 50 140 63 140 80C140 97 127 110 110 110H60" stroke="#00C58E" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VobizLogo = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="40" fill="#0B1221" />
    <path d="M55 55L100 145L145 55" stroke="#5B8DEF" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Renders a real logo file from `public/logos/<logoKey>.{svg,png}`. Tries SVG first,
// then PNG, and finally falls back to the inline SVG above if no file is present —
// so dropping a file into public/logos/ is all that's needed to upgrade a logo.
//   e.g. logoKey "vapi"  →  public/logos/vapi.svg  (or vapi.png)
const VendorLogo = ({ logoKey, alt, Fallback, className = "h-8 w-8" }) => {
  const EXTS = ["svg", "png"];
  const [extIdx, setExtIdx] = useState(0);
  if (!logoKey || extIdx >= EXTS.length) {
    return Fallback ? <Fallback className={className} /> : null;
  }
  return (
    <img
      src={`${process.env.PUBLIC_URL || ""}/logos/${logoKey}.${EXTS[extIdx]}`}
      alt={alt || logoKey}
      className={`${className} object-contain rounded-lg`}
      onError={() => setExtIdx((i) => i + 1)}
    />
  );
};

const TRANSPORT_PROVIDERS = [
  {
    provider: "livekit",
    name: "LiveKit",
    logoKey: "livekit",
    Logo: LiveKitLogo,
    description: "Workspace default for LiveKit rooms. Agents can override the URL/key/secret and add room config per-agent under Channels → Web.",
    fields: [
      { key: "url", label: "LiveKit URL", type: "text", required: true, placeholder: "wss://your-project.livekit.cloud" },
      { key: "api_key", label: "API Key", type: "password", required: true, placeholder: "Enter your LiveKit API key" },
      { key: "api_secret", label: "API Secret", type: "password", required: true, placeholder: "Enter your LiveKit API secret" },
    ],
  },
  {
    provider: "daily",
    name: "Daily",
    logoKey: "daily",
    Logo: DailyLogo,
    description: "Workspace default for Daily rooms (Pipecat/Daily agents). Agents can override the API key and set room properties (incl. recording) per-agent under Channels → Web.",
    fields: [
      { key: "api_key", label: "API Key", type: "password", required: true, placeholder: "Enter your Daily API key" },
    ],
  },
];

const TELEPHONY_PROVIDERS = [
  {
    provider: "plivo",
    name: "Plivo",
    category: "telephony",
    logoKey: "plivo",
    Logo: PlivoLogo,
    description: "Bring your own Plivo account. Once configured, agents using the Plivo carrier place outbound calls from this account, and your declared concurrency limit is enforced.",
    fields: [
      { key: "auth_id", label: "Auth ID", type: "text", required: true, placeholder: "Enter your Plivo Auth ID" },
      { key: "auth_token", label: "Auth Token", type: "password", required: true, placeholder: "Enter your Plivo Auth Token" },
    ],
    settingsFields: [
      { key: "from_numbers", label: "From numbers (comma-separated)", type: "text", placeholder: "+15551234567, +15557654321" },
      { key: "max_concurrent", label: "Max concurrent", type: "number", placeholder: "500" },
    ],
  },
  {
    provider: "vobiz",
    name: "Vobiz",
    category: "telephony",
    logoKey: "vobiz",
    Logo: VobizLogo,
    description: "Bring your own Vobiz (vobiz.ai) account. Once configured, agents using the Vobiz carrier place outbound calls from this account, and your declared concurrency limit is enforced.",
    fields: [
      { key: "auth_id", label: "Auth ID", type: "text", required: true, placeholder: "Enter your Vobiz Auth ID" },
      { key: "auth_token", label: "Auth Token", type: "password", required: true, placeholder: "Enter your Vobiz Auth Token" },
    ],
    settingsFields: [
      { key: "from_numbers", label: "From numbers (comma-separated)", type: "text", placeholder: "+919000000001, +919000000002" },
      { key: "max_concurrent", label: "Max concurrent", type: "number", placeholder: "500" },
    ],
  },
];

const VOICE_PLATFORMS = [
  {
    provider: "vapi",
    name: "Vapi",
    logoKey: "vapi",
    Logo: VapiLogo,
    description: "Connect your Vapi account to sync assistants and import calls.",
    fields: [
      { key: "api_key", label: "Private Key", type: "password", required: true, placeholder: "Enter your Vapi private key" },
    ],
  },
  {
    provider: "retell",
    name: "Retell",
    logoKey: "retell",
    Logo: RetellLogo,
    description: "Connect your Retell account to sync agents and import calls.",
    fields: [
      { key: "api_key", label: "API Key", type: "password", required: true, placeholder: "Enter your Retell API key" },
    ],
  },
];

const NOTIFICATION_TYPES = [
  {
    channel_type: "slack",
    name: "Slack",
    logoKey: "slack",
    Logo: SlackLogo,
    description: "Connect your Slack workspace and select a channel for alerts.",
    connectMode: "oauth", // special flow
  },
  {
    channel_type: "email",
    name: "Email",
    logoKey: "email",
    Logo: EmailLogo,
    description: "Send alert emails to one or more recipients.",
    connectLabel: "Add Emails",
    fields: [
      { key: "recipients", label: "Recipients (comma-separated)", type: "text", required: true, placeholder: "team@company.com, alerts@company.com" },
    ],
  },
  {
    channel_type: "webhook",
    name: "Webhook",
    logoKey: "webhook",
    Logo: WebhookLogo,
    description: "POST alert payloads to your own HTTP endpoint.",
    fields: [
      { key: "url", label: "Endpoint URL", type: "url", required: true, placeholder: "https://api.example.com/webhooks/alerts" },
      { key: "secret", label: "Signing Secret (optional)", type: "password", placeholder: "Used for HMAC-SHA256 signature verification" },
    ],
  },
];

// ── Status badge ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const styles = {
    connected: "bg-green-50 text-green-700 border-green-200",
    error: "bg-red-50 text-red-700 border-red-200",
    disconnected: "bg-muted text-muted-foreground border-border",
  };
  const labels = { connected: "Connected", error: "Error", disconnected: "Not connected" };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${styles[status] || styles.disconnected}`}>
      {labels[status] || "Not connected"}
    </span>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const IntegrationsSettings = () => {
  const { currentWorkspace } = useAuth();
  const clientId = currentWorkspace?.id;
  const canWrite = useCan("integration_config", "write");

  const [integrations, setIntegrations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null); // provider string or channel_type
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [message, setMessage] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});

  // Slack OAuth state
  const [slackConnecting, setSlackConnecting] = useState(false);
  const [slackChannels, setSlackChannels] = useState([]);
  const [slackChannelsLoading, setSlackChannelsLoading] = useState(false);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!clientId) return;
    try {
      const [intData, chData] = await Promise.all([
        clientIntegrationsAPI.list(clientId),
        notificationChannelsAPI.list(clientId),
      ]);
      setIntegrations(intData);
      setChannels(chData);
    } catch (e) {
      console.error("Failed to load integrations", e);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const [searchParams] = useSearchParams();

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Handle Slack OAuth callback — Slack redirects back here with ?code=...&state=...
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    if (searchParams.get("slack_callback") === "true" && code && clientId) {
      // Exchange the code for access token
      (async () => {
        try {
          await notificationChannelsAPI.slackCallback(clientId, code, state);
          flash("Slack workspace connected! Now select a channel.");
          fetchAll();
          loadSlackChannels();
        } catch (err) {
          console.error("Slack callback error:", err);
          flash(err.response?.data?.error || "Failed to complete Slack connection", "error");
        }
        // Clean up URL params
        const cleanUrl = new URL(window.location);
        cleanUrl.searchParams.delete("code");
        cleanUrl.searchParams.delete("state");
        cleanUrl.searchParams.delete("slack_callback");
        window.history.replaceState({}, "", cleanUrl.toString());
      })();
    }
  }, [searchParams, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const openForm = (key) => {
    setActiveForm(key);
    setEditingId(null);
    setFormData({});
    setShowSecrets({});
  };

  const closeForm = () => {
    setActiveForm(null);
    setEditingId(null);
    setFormData({});
  };

  const toggleSecret = (key) => setShowSecrets((s) => ({ ...s, [key]: !s[key] }));

  // ── Voice Platform handlers ────────────────────────────────────────────
  const handleSaveIntegration = async (providerDef) => {
    // Validate required fields
    const missing = providerDef.fields.filter((f) => f.required && !formData[f.key]?.trim());
    if (missing.length > 0) {
      flash(`${missing.map((f) => f.label).join(", ")} required`, "error");
      return;
    }

    setSaving(true);
    try {
      // Credentials come from `fields`; non-secret `settingsFields` (if any) are
      // sent separately under `settings` — mirrors the backend's credentials/settings split.
      const credentials = {};
      providerDef.fields.forEach((f) => {
        if (formData[f.key] !== undefined && formData[f.key] !== "") credentials[f.key] = formData[f.key];
      });

      const payload = { provider: providerDef.provider, credentials, enabled: true };

      if (providerDef.settingsFields?.length) {
        const settings = {};
        providerDef.settingsFields.forEach((f) => {
          const v = formData[f.key];
          if (v === undefined || v === "") return;
          settings[f.key] = f.type === "number" ? Number(v) : v;
        });
        if (Object.keys(settings).length > 0) payload.settings = settings;
      }

      if (editingId) {
        await clientIntegrationsAPI.update(clientId, editingId, payload);
        flash(`${providerDef.name} updated`);
      } else {
        await clientIntegrationsAPI.create(clientId, payload);
        flash(`${providerDef.name} connected`);
      }
      closeForm();
      fetchAll();
    } catch (e) {
      flash(e.response?.data?.errors?.join(", ") || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTestIntegration = async (id) => {
    setTesting(id);
    try {
      const res = await clientIntegrationsAPI.testConnection(clientId, id);
      flash(res.message || "Connection verified", res.success ? "success" : "error");
      fetchAll();
    } catch (e) {
      flash(e.response?.data?.message || e.message, "error");
    } finally {
      setTesting(null);
    }
  };

  const handleDeleteIntegration = async (id, name) => {
    if (!window.confirm(`Disconnect ${name}?`)) return;
    try {
      await clientIntegrationsAPI.destroy(clientId, id);
      flash(`${name} disconnected`);
      fetchAll();
    } catch (e) {
      flash(e.message, "error");
    }
  };

  // ── Notification Channel handlers ──────────────────────────────────────
  const handleSaveChannel = async (typeDef) => {
    // Validate required fields
    if (typeDef.fields) {
      const missing = typeDef.fields.filter((f) => f.required && !formData[f.key]?.trim());
      if (missing.length > 0) {
        flash(`${missing.map((f) => f.label).join(", ")} required`, "error");
        return;
      }
    }

    setSaving(true);
    try {
      const config = { ...formData };
      // Parse comma-separated emails into array
      if (typeDef.channel_type === "email" && typeof config.recipients === "string") {
        config.recipients = config.recipients.split(",").map((e) => e.trim()).filter(Boolean);
      }

      // Auto-generate name from config
      let name;
      if (typeDef.channel_type === "email") {
        const recipients = config.recipients || [];
        name = recipients.length === 1 ? recipients[0] : `${recipients.length} recipients`;
      } else if (typeDef.channel_type === "webhook") {
        try {
          name = new URL(config.url).hostname;
        } catch {
          name = "Webhook";
        }
      } else if (typeDef.channel_type === "slack") {
        name = config.channel || "Slack";
      }

      const payload = {
        channel_type: typeDef.channel_type,
        name,
        config,
        enabled: true,
      };

      if (editingId) {
        await notificationChannelsAPI.update(clientId, editingId, payload);
        flash(`${typeDef.name} channel updated`);
      } else {
        await notificationChannelsAPI.create(clientId, payload);
        flash(`${typeDef.name} channel added`);
      }
      closeForm();
      fetchAll();
    } catch (e) {
      flash(e.response?.data?.errors?.join(", ") || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSlackConnect = async () => {
    setSlackConnecting(true);
    try {
      const { url } = await notificationChannelsAPI.slackAuthUrl(clientId);
      // Redirect in the same window — Slack will redirect back with ?code=...&state=...
      window.location.href = url;
    } catch (err) {
      console.error("Slack OAuth error:", err);
      flash(err.response?.data?.error || err.response?.data?.errors?.join(", ") || "Failed to connect to Slack. Check that SLACK_CLIENT_ID is configured.", "error");
      setSlackConnecting(false);
    }
  };

  const loadSlackChannels = async () => {
    setSlackChannelsLoading(true);
    setActiveForm("slack_channels");
    try {
      const { channels } = await notificationChannelsAPI.slackChannels(clientId);
      setSlackChannels(channels);
    } catch (err) {
      flash(err.response?.data?.error || "Failed to load Slack channels", "error");
      setActiveForm(null);
    } finally {
      setSlackChannelsLoading(false);
    }
  };

  const handleSlackChannelSelect = async (channel) => {
    setSaving(true);
    try {
      await notificationChannelsAPI.slackSelectChannel(clientId, channel.id, channel.name);
      flash(`Slack channel #${channel.name} selected`);
      setActiveForm(null);
      setSelectedSlackChannel(null);
      fetchAll();
    } catch (err) {
      flash(err.response?.data?.error || err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTestChannel = async (id) => {
    setTesting(id);
    try {
      const res = await notificationChannelsAPI.test(clientId, id);
      flash(res.message || "Test sent", res.success ? "success" : "error");
    } catch (e) {
      flash(e.response?.data?.message || e.message, "error");
    } finally {
      setTesting(null);
    }
  };

  const handleDeleteChannel = async (id, name) => {
    if (!window.confirm(`Remove "${name}" channel?`)) return;
    try {
      await notificationChannelsAPI.destroy(clientId, id);
      flash(`"${name}" removed`);
      fetchAll();
    } catch (e) {
      flash(e.message, "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Render integration card (shared layout for voice + notifications) ──
  const renderCard = ({ Logo, logoKey, name, description, status, isConnected, onConnect, onTest, onEdit, onDisconnect, testingId, existingId, connectLabel, connectButton, children }) => (
    <div className="border border-border rounded-lg p-4 hover:border-input transition-colors">
      <div className="flex items-start gap-4">
        <div className="shrink-0">
          <VendorLogo logoKey={logoKey} alt={name} Fallback={Logo} className="h-10 w-10" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-semibold text-foreground">{name}</span>
            {isConnected && <StatusBadge status={status} />}
          </div>
          <p className="text-xs text-muted-foreground">{description}</p>

          {isConnected ? (
            <div className="mt-3 flex items-center gap-3">
              {onTest && canWrite && (
                <button onClick={onTest} disabled={testing === testingId} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                  {testing === testingId ? <Loader2 className="h-3 w-3 animate-spin" /> : <TestTube className="h-3 w-3" />}
                  Test
                </button>
              )}
              {onEdit && canWrite && (
                <button onClick={onEdit} className="text-xs text-muted-foreground hover:text-foreground">Edit</button>
              )}
              {onDisconnect && canWrite && (
                <button onClick={onDisconnect} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                  <Unlink className="h-3 w-3" /> Disconnect
                </button>
              )}
            </div>
          ) : canWrite && connectButton ? (
            connectButton
          ) : canWrite ? (
            <button onClick={onConnect} className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1">
              <Link2 className="h-4 w-4" /> {connectLabel || "Connect"}
            </button>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );

  // ── Inline form for credentials ──────────────────────────────────────
  const renderFieldsForm = ({ fields, settingsFields, onSave, saveLabel = "Connect" }) => (
    <div className="mt-4 border-t border-border pt-4 space-y-3 ml-14">
      {fields.map((f) => (
        <div key={f.key}>
          <label className="text-xs font-medium text-muted-foreground block mb-1">{f.label}</label>
          <div className="relative">
            <input
              type={showSecrets[f.key] ? "text" : f.type}
              value={formData[f.key] || ""}
              onChange={(e) => setFormData((d) => ({ ...d, [f.key]: e.target.value }))}
              placeholder={f.placeholder || `Enter ${f.label.toLowerCase()}`}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-10"
            />
            {f.type === "password" && (
              <button type="button" onClick={() => toggleSecret(f.key)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground">
                {showSecrets[f.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
      ))}
      {settingsFields?.length > 0 && (
        <div className="pt-1 space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Settings</p>
          {settingsFields.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-medium text-muted-foreground block mb-1">{f.label}</label>
              <input
                type={f.type}
                value={formData[f.key] || ""}
                onChange={(e) => setFormData((d) => ({ ...d, [f.key]: e.target.value }))}
                placeholder={f.placeholder || `Enter ${f.label.toLowerCase()}`}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={onSave} disabled={saving} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
          {editingId ? "Update" : saveLabel}
        </button>
        <button onClick={closeForm} className="px-4 py-2 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted/50">
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Flash message */}
      {message && (
        <div className={`rounded-lg px-4 py-3 text-sm flex items-center gap-2 ${message.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
          {message.type === "error" ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {message.text}
        </div>
      )}

      {/* ── Voice Platforms ─────────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-1">Voice Platforms</h3>
        <p className="text-sm text-muted-foreground mb-4">Connect your external voice AI platform to sync agents and import calls.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {VOICE_PLATFORMS.map((def) => {
            const existing = integrations.find((i) => i.provider === def.provider);
            return renderCard({
              key: def.provider,
              Logo: def.Logo,
              logoKey: def.logoKey,
              name: def.name,
              description: def.description,
              status: existing?.status,
              isConnected: !!existing,
              testingId: existing?.id,
              existingId: existing?.id,
              onConnect: () => openForm(def.provider),
              onTest: existing ? () => handleTestIntegration(existing.id) : null,
              onEdit: existing ? () => { setActiveForm(def.provider); setEditingId(existing.id); setFormData({}); } : null,
              onDisconnect: existing ? () => handleDeleteIntegration(existing.id, def.name) : null,
              children: activeForm === def.provider && renderFieldsForm({
                fields: def.fields,
                onSave: () => handleSaveIntegration(def),
              }),
            });
          })}
        </div>
      </section>

      {/* ── Transport Providers ──────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-1">Transport Providers</h3>
        <p className="text-sm text-muted-foreground mb-4">Connect your WebRTC transport provider for web-based agent testing. Credentials here are used as workspace defaults — agents can override per-agent.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TRANSPORT_PROVIDERS.map((def) => {
            const existing = integrations.find((i) => i.provider === def.provider);
            return renderCard({
              key: def.provider,
              Logo: def.Logo,
              logoKey: def.logoKey,
              name: def.name,
              description: def.description,
              status: existing?.status,
              isConnected: !!existing,
              testingId: existing?.id,
              existingId: existing?.id,
              onConnect: () => openForm(def.provider),
              onTest: existing ? () => handleTestIntegration(existing.id) : null,
              onEdit: existing ? () => { setActiveForm(def.provider); setEditingId(existing.id); setFormData({}); } : null,
              onDisconnect: existing ? () => handleDeleteIntegration(existing.id, def.name) : null,
              children: activeForm === def.provider && renderFieldsForm({
                fields: def.fields,
                onSave: () => handleSaveIntegration(def),
              }),
            });
          })}
        </div>
      </section>

      {/* ── Telephony Providers ──────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-1">Telephony Providers</h3>
        <p className="text-sm text-muted-foreground mb-4">Bring your own carrier for outbound phone calls. Credentials here are used as the workspace default; runs draw from this carrier's capacity.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TELEPHONY_PROVIDERS.map((def) => {
            const existing = integrations.find((i) => i.provider === def.provider);
            return renderCard({
              key: def.provider,
              Logo: def.Logo,
              logoKey: def.logoKey,
              name: def.name,
              description: def.description,
              status: existing?.status,
              isConnected: !!existing,
              testingId: existing?.id,
              existingId: existing?.id,
              onConnect: () => openForm(def.provider),
              onTest: existing ? () => handleTestIntegration(existing.id) : null,
              onEdit: existing ? () => { setActiveForm(def.provider); setEditingId(existing.id); setFormData({}); } : null,
              onDisconnect: existing ? () => handleDeleteIntegration(existing.id, def.name) : null,
              children: (
                <>
                  {activeForm === def.provider && renderFieldsForm({
                    fields: def.fields,
                    settingsFields: def.settingsFields,
                    onSave: () => handleSaveIntegration(def),
                  })}
                </>
              ),
            });
          })}
        </div>
      </section>

      {/* ── Notification Channels ──────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-1">Notification Channels</h3>
        <p className="text-sm text-muted-foreground mb-4">Configure where alert notifications are delivered.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {NOTIFICATION_TYPES.map((def) => {
            const existingChannels = channels.filter((ch) => ch.channel_type === def.channel_type);
            const isSlack = def.channel_type === "slack";
            const hasExisting = existingChannels.length > 0;

            // Slack gets a custom card with OAuth button
            if (isSlack) {
              return (
                <div key={def.channel_type}>
                  {renderCard({
                    Logo: def.Logo,
                    logoKey: def.logoKey,
                    name: def.name,
                    description: def.description,
                    status: hasExisting ? "connected" : "disconnected",
                    isConnected: hasExisting,
                    onTest: hasExisting && existingChannels[0]?.config?.channel_id ? () => handleTestChannel(existingChannels[0].id) : null,
                    onEdit: hasExisting ? () => loadSlackChannels() : null,
                    onDisconnect: hasExisting ? () => handleDeleteChannel(existingChannels[0].id, existingChannels[0].name) : null,
                    testingId: existingChannels[0]?.id,
                    // Custom connect button for Slack
                    connectButton: (
                      <div className="mt-3 space-y-2">
                        <button
                          onClick={handleSlackConnect}
                          disabled={slackConnecting}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                          style={{ backgroundColor: "#4A154B" }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#611f69"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#4A154B"}
                        >
                          {slackConnecting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.271 0a2.528 2.528 0 0 1-2.521 2.521 2.528 2.528 0 0 1-2.521-2.521V2.522A2.528 2.528 0 0 1 15.164 0a2.528 2.528 0 0 1 2.521 2.522v6.312zM15.164 18.956a2.528 2.528 0 0 1 2.521 2.522A2.528 2.528 0 0 1 15.164 24a2.528 2.528 0 0 1-2.521-2.522v-2.522h2.521zm0-1.271a2.528 2.528 0 0 1-2.521-2.521 2.528 2.528 0 0 1 2.521-2.521h6.314A2.528 2.528 0 0 1 24 15.164a2.528 2.528 0 0 1-2.522 2.521h-6.314z"/>
                            </svg>
                          )}
                          Connect Slack Workspace
                        </button>
                        <button
                          onClick={() => openForm("slack_manual")}
                          className="block text-xs text-muted-foreground hover:text-muted-foreground"
                        >
                          Or connect with webhook URL
                        </button>
                      </div>
                    ),
                    children: (
                      <>
                        {/* Connected channel info */}
                        {hasExisting && existingChannels[0]?.config?.team_name && (
                          <div className="mt-2 ml-14 text-xs text-muted-foreground">
                            Workspace: <span className="font-medium text-foreground">{existingChannels[0].config.team_name}</span>
                            {existingChannels[0].config.channel_name && (
                              <> &middot; Channel: <span className="font-medium text-foreground">#{existingChannels[0].config.channel_name}</span></>
                            )}
                          </div>
                        )}

                        {/* Additional channels list */}
                        {existingChannels.length > 1 && (
                          <div className="mt-3 ml-14 space-y-1.5">
                            {existingChannels.slice(1).map((ch) => (
                              <div key={ch.id} className="flex items-center justify-between text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1">
                                <span>{ch.name}</span>
                                <div className="flex gap-2">
                                  <button onClick={() => handleTestChannel(ch.id)} disabled={testing === ch.id} className="text-indigo-600 hover:text-indigo-800">
                                    {testing === ch.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Test"}
                                  </button>
                                  <button onClick={() => handleDeleteChannel(ch.id, ch.name)} className="text-red-500 hover:text-red-700">
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Slack OAuth channel selector */}
                        {activeForm === "slack_channels" && (
                          <div className="mt-4 border-t border-border pt-4 ml-14">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-muted-foreground">Select a channel for alerts</label>
                              <button
                                type="button"
                                onClick={loadSlackChannels}
                                disabled={slackChannelsLoading}
                                className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50"
                              >
                                {slackChannelsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                Refresh
                              </button>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              Looking for a private channel? Type <code className="px-1 py-0.5 bg-muted rounded text-muted-foreground">/invite @RubricHQ</code> in that Slack channel first, then click Refresh.
                            </p>
                            {slackChannelsLoading ? (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading channels...
                              </div>
                            ) : (
                              <>
                                <select
                                  value={selectedSlackChannel?.id || ""}
                                  onChange={(e) => {
                                    const ch = slackChannels.find((c) => c.id === e.target.value);
                                    setSelectedSlackChannel(ch || null);
                                  }}
                                  className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                  <option value="">Choose a channel...</option>
                                  {slackChannels.map((ch) => (
                                    <option key={ch.id} value={ch.id}>
                                      {ch.is_private ? "🔒 " : "#"}{ch.name}
                                    </option>
                                  ))}
                                </select>
                                <div className="flex gap-2 pt-3">
                                  <button
                                    onClick={() => selectedSlackChannel && handleSlackChannelSelect(selectedSlackChannel)}
                                    disabled={!selectedSlackChannel || saving}
                                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                                  >
                                    {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                                    Save Channel
                                  </button>
                                  <button onClick={closeForm} className="px-4 py-2 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted/50">
                                    Cancel
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* Manual Slack webhook fallback */}
                        {activeForm === "slack_manual" && renderFieldsForm({
                          fields: [
                            { key: "webhook_url", label: "Slack Webhook URL", type: "url", required: true, placeholder: "https://hooks.slack.com/services/..." },
                            { key: "channel", label: "Channel Name (optional)", type: "text", placeholder: "#alerts" },
                          ],
                          onSave: () => handleSaveChannel(def),
                          saveLabel: "Add Channel",
                        })}
                      </>
                    ),
                  })}
                </div>
              );
            }

            // Email & Webhook cards
            return (
              <div key={def.channel_type}>
                {renderCard({
                  Logo: def.Logo,
                  logoKey: def.logoKey,
                  name: def.name,
                  description: def.description,
                  status: hasExisting ? "connected" : "disconnected",
                  isConnected: hasExisting,
                  onConnect: () => openForm(def.channel_type),
                  onTest: hasExisting ? () => handleTestChannel(existingChannels[0].id) : null,
                  onDisconnect: hasExisting ? () => handleDeleteChannel(existingChannels[0].id, existingChannels[0].name) : null,
                  testingId: existingChannels[0]?.id,
                  connectLabel: def.connectLabel,
                  children: (
                    <>
                      {existingChannels.length > 1 && (
                        <div className="mt-3 ml-14 space-y-1.5">
                          {existingChannels.slice(1).map((ch) => (
                            <div key={ch.id} className="flex items-center justify-between text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1">
                              <span>{ch.name}</span>
                              <div className="flex gap-2">
                                <button onClick={() => handleTestChannel(ch.id)} disabled={testing === ch.id} className="text-indigo-600 hover:text-indigo-800">
                                  {testing === ch.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Test"}
                                </button>
                                <button onClick={() => handleDeleteChannel(ch.id, ch.name)} className="text-red-500 hover:text-red-700">
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {activeForm === def.channel_type && def.fields && renderFieldsForm({
                        fields: def.fields,
                        onSave: () => handleSaveChannel(def),
                        saveLabel: "Add Channel",
                      })}
                    </>
                  ),
                })}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default IntegrationsSettings;
